import * as R from "ramda";
import React, { useCallback, useEffect, useState } from 'react';

import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { FaCalculator, FaRedo } from 'react-icons/fa';
import { Button } from 'react-bootstrap';

import useAlertNotification from '../../../context/AlertNotificationContext';
import FechamentoApiService from '../../../services/api/FechamentoApiService';
import ErrorRequestService from '../../../services/errorRequest';
import ImpostoApiService from '../../../services/api/ImpostoApiService';
import BeneficioApiService from '../../../services/api/BeneficioApiService';

import Loading from '../../../components/Loading';
import PageContainer from '../../../components/PageContainer';
import ListLayout from '../../../components/ListLayout';

import { columns } from './FechamentoPageListInfo';
import FechamentoView from '../FechamentoView';
import FechamentoPageDetail from './FechamentoPageDetail';

import UtilService from '../../../services/util';

import './FechamentoPage.css';

const ORDER_DIR_NUM = { asc: 1, desc: -1 };

const INITIAL_PAGEPARAMS = {
    orderBy: 'nome',
    orderDir: 'asc'
};

const withCustoInterno = (rows) => rows.map(item => ({
    ...item,
    custoInterno: R.sum([
        Number(item.custoTotal) || 0,
        Number(item.impostoTotal) || 0,
        -(Number(item.salarioBase) || 0)
    ]),
}));

function FechamentoPage({ onRowClick }) {
    // HOOKS
    const history = useHistory();
    const match = useRouteMatch();
    const location = useLocation();
    const { id, label } = location.state;
    const alert = useAlertNotification();

    // STATE
    const [partTimeColab, setPartTimeColab] = useState([]);
    const [rowsData, setRowsData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isCalculando, setIsCalculando] = useState(false);
    const [filteredData, setFilteredData] = useState(null);
    const [orderBy, setOrderBy] = useState(INITIAL_PAGEPARAMS.orderBy);
    const [orderDir, setOrderDir] = useState(INITIAL_PAGEPARAMS.orderDir);

    // FUNCTIONS
    /** Define o clique sobre um registro da lista */
    const rowClick = (item) => {
        if (onRowClick) {
            onRowClick(item);
            return;
        }
        history.push(`${match.url}/${item.cod}`, item);
    }

    /** Adiciona click e atributos ao header */
    const getListHeader = () => {
        const headers = [];
        const defineOrder = (item) => {
            if (orderBy === item) {
                const newDir = (orderDir === 'asc') ? 'desc' : 'asc';
                setOrderDir(newDir);
                return;
            }
            setOrderBy(item);
        }
        columns.forEach(item => {
            const waySign = (ORDER_DIR_NUM[orderDir] > 0) ? '▴' : '▾';
            const orderInd = (orderBy === item.name)
                ? { ordered: `${waySign}` }
                : {};
            const head = {
                ...item,
                ...orderInd,
                // onClick: () => defineOrder(item.name),
            }
            headers.push(head);
        })
        return headers;
    }

    const defineDataList = () => {
        const sortRows = (a, b) => {
            if (a[orderBy] < b[orderBy]) return (-1 * ORDER_DIR_NUM[orderDir]);
            if (a[orderBy] > b[orderBy]) return (1 * ORDER_DIR_NUM[orderDir]);
        }

        const dataList = [];
        const rows = filteredData || rowsData || [];

        rows.sort(sortRows)
            .forEach(item => {
                const row = {
                    // onClick: () => rowClick(item),
                    data: columns.map(col => {
                        let value = item[col.name];

                        if (col.name !== 'nome' && col.name !== 'matricula' && col.name !== 'cargaHoraria')
                            value = value ? value.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' }) : '0';

                        if(col.name === 'cargaHoraria'){
                            value = value ? `${value}h` : ""
                        }
                        
                        return {
                            value,
                            className: col.className
                        }

                    }),
                };
                dataList.push(row);
            });
        return dataList;
    }

    /* Filtra a lista de dados */
    const filterList = (params) => {
        if (params.length <= 0 || !Array.isArray(params)) {
            setFilteredData(rowsData); // filtro local

            return
        }

        // APLICA FILTRO LOCAL
        let filterData = rowsData;

        const columns = {}

        // Formata os parametros para transformar em um objeto
        // Exemplo: { departamento: ['financ', 'rnp']}
        params.forEach((item) => {
            if (columns.hasOwnProperty(item.column))
                columns[item.column] = [...columns[item.column], item.filter]
            else
                columns[item.column] = [item.filter]
        })

        Object.keys(columns).map(columnKey =>
            filterData = filterByParam(filterData, columnKey, columns[columnKey])
        )

        const notDuplicate = filterData.reduce((accum, curr) => {
            if (accum.length <= 0) return [curr];

            const notSame = accum.filter(item => item.matricula !== curr.matricula);
            return [...notSame, curr];
        }, []);

        setFilteredData(notDuplicate);
    }

    const filterByParam = (arr, column, arrFilter) => {
        if (!column || !arrFilter) return arr;

        return arr.filter(item => {
          const lwData = String(item[column]).toLowerCase();
          return arrFilter.some(teste => lwData.includes(String(teste).toLowerCase()));
        })
      }

    const exportDataInExcel = () => {
        const transformFormatNumber = (number) => {
            return parseFloat(parseFloat(number).toFixed(2));
        }

        const exportColumns = [
            ['id', 'matricula'],
            ['Colaborador', 'nome'],
            ['Departamento', 'departamento'],
            ['Função', 'funcao'],
            ['Regime', 'regime'],
            ['Salário', 'salarioBase', transformFormatNumber],
            ['Salário Líquido', 'salarioLiquido', transformFormatNumber],
            ['Custo Folha', 'salarioFilial', transformFormatNumber],
            ['Benefícios', 'beneficioTotal', transformFormatNumber],
            ['Custos Adicionais', 'custoAdicionalTotal', transformFormatNumber],
            ['Impostos', 'impostoTotal', transformFormatNumber],
            ['Provisão 13º', 'provisaoDecimoTerceiro', transformFormatNumber],
            ['Provisão Rescisão', 'provisaoRescisao', transformFormatNumber],
            ['Provisão Férias', 'provisaoFerias', transformFormatNumber],
            ['Custo Total S/ Imposto', 'custoTotal', transformFormatNumber],
            ['Custo Interno', 'custoInterno', transformFormatNumber],
            ['Custo Hora', 'custoHora', transformFormatNumber],
            ['Carga Horária', 'cargaHoraria'],
        ];

        const header = [exportColumns.map(([title]) => title)];

        const formatData = filteredData === null ? rowsData : filteredData;

        const exportRows = formatData.map(item => Object.fromEntries(
            exportColumns.map(([title, campo, format]) => [
                title,
                format ? format(item[campo]) : item[campo]
            ])
        ));

        UtilService.exportXls(exportRows, label, header);
    }

    const formatFechamentoResult = () => {
        const formatData = filteredData === null ? rowsData : filteredData;

        const initial = {
            custoTTotal: 0,
            custoInternoTotal: 0,
            beneficioTTotal: 0,
            custoAdicionalTotal: 0,
            custoHHTotal: 0,
            impostoTTotal: 0,
            provisaoDecimoTerceiroTotal: 0,
            provisaoFeriasTotal: 0,
            provisaoRescisaoTotal: 0,
            salarioBaseTotal: 0,
            salarioFilialTotal: 0,
            salarioLiquidoTotal: 0,
            quantidadeColabs: 0
        }

        const calculated = formatData.reduce((accum, curr) => {
            return {
                custoTTotal: R.sum([accum.custoTTotal, curr.custoTotal]),
                custoInternoTotal: R.sum([accum.custoInternoTotal, curr.custoInterno]),
                beneficioTTotal: R.sum([accum.beneficioTTotal, curr.beneficioTotal]),
                custoAdicionalTotal: R.sum([accum.custoAdicionalTotal, curr.custoAdicionalTotal]),
                custoHHTotal: R.sum([accum.custoHHTotal, curr.custoHora]),
                impostoTTotal: R.sum([accum.impostoTTotal, curr.impostoTotal]),
                provisaoDecimoTerceiroTotal: R.sum([accum.provisaoDecimoTerceiroTotal, curr.provisaoDecimoTerceiro]),
                provisaoFeriasTotal: R.sum([accum.provisaoFeriasTotal, curr.provisaoFerias]),
                provisaoRescisaoTotal: R.sum([accum.provisaoRescisaoTotal, curr.provisaoRescisao]),
                salarioBaseTotal: R.sum([accum.salarioBaseTotal, curr.salarioBase]),
                salarioFilialTotal: R.sum([accum.salarioFilialTotal, curr.salarioFilial]),
                salarioLiquidoTotal: R.sum([accum.salarioLiquidoTotal, curr.salarioLiquido]),
                quantidadeColabs: R.sum([accum.quantidadeColabs, 1])
            }
        }, initial);

        return calculated
    }

    // FUNCTIONS
    const handleCalculate = useCallback(async (isRecalculate = false) => {
        const confirma = !isRecalculate || window.confirm(
            'Os cálculos totais gerados anteriormente para este fechamento serão DESCARTADOS.'
            + '\nNovos cálculos serão gerados com base nos valores atuais de impostos, dados de colaboradores e seus benefícos.'
            + '\n\n*** ATENÇÃO: Esta operação não é reversível! ***\n'
            + '\nDeseja realmente continuar?'
        );
        if (confirma) {
            setIsCalculando(true);
            try {
                const [impCalc, benefCalc] = await Promise.all([
                    ImpostoApiService.calculate(id, isRecalculate).then(r => r.data),
                    BeneficioApiService.calculate(id, isRecalculate).then(r => r.data)
                ]);
    
                if (impCalc && benefCalc) {
                    await FechamentoApiService.calculoCustoHora(id, partTimeColab).then(r => r.data);

                    const custosColab = await FechamentoApiService.getCustoHH(id).then(r => r.data);

                    setRowsData(withCustoInterno(custosColab));

                    history.replace(match.url, location.state);
                }
            }
            catch (err) {
                const errorService = new ErrorRequestService(err);
                const errorMsg = errorService.getErrors();
                console.log(errorMsg);
                alert.error(errorMsg);
            }
            finally {
                setIsCalculando(false);
            }
        }
    }, [id, history, location.state, match.url, partTimeColab]);

    const onStart = useCallback(async () => {
        setIsLoading(true);

        try {
            const custosColab = await FechamentoApiService.getCustoHH(id).then(r => r.data);

            setRowsData(withCustoInterno(custosColab));

            const colabMeioPeriodo = []

            custosColab.forEach(item => {
                if(item.meioPeriodo === 1)
                    colabMeioPeriodo.push({ id: item.matricula, checked: true });
            })

            setPartTimeColab(colabMeioPeriodo)
        }
        catch (err) {
            const errorService = new ErrorRequestService(err);
            const errorMsg = errorService.getErrors();
            console.log(errorMsg);
            alert.error(errorMsg);
        }
        finally {
            setIsLoading(false);
        }
    }, [id]);

    // USEEFFECTS
    useEffect(onStart, []);

    const actions = [
        ...(rowsData.length
            ? [{
                ...(isCalculando
                    ? { label: 'Recalculando...' }
                    : { label: 'Recalcular', icon: <FaRedo /> }
                ),
                color: 'info',
                type: 'button',
                disabled: isCalculando,
                onClick: !isCalculando ? () => handleCalculate(true) : null
            }]
            : [{
                ...(isCalculando
                    ? { label: 'Calculando...' }
                    : { label: 'Calcular', icon: <FaCalculator /> }
                ),
                color: 'dark',
                type: 'button',
                disabled: isCalculando,
                onClick: !isCalculando ? () => handleCalculate() : null
            }]
        )
    ]

    return (
        <div className='fechamento-container'>
            {isLoading && (
                <PageContainer title={`Fechamento ${label || ''}`}>
                    <Loading message="Carregando..." />
                </PageContainer>
            )}

            {!isLoading && (<>
                <FechamentoView
                    extraActions={actions}
                    key={`fecham-view-${location.key}`}
                />

                <Button
                    style={{ marginBottom: 20 }}
                    onClick={exportDataInExcel}
                >Exportar</Button>

                <FechamentoPageDetail fechamento={formatFechamentoResult()} />
                <div style={{ width: '100%', maxWidth: 'calc(100vw - 280px)' }}>
                    <ListLayout
                        columns={getListHeader()}
                        rows={defineDataList()}
                        onSubmitFilter={filterList}
                        multiFilter
                        stickyHeader
                    />
                </div>
                
            </>)}
        </div>
    )
}

export default FechamentoPage
